import { Link } from '@tanstack/react-router';
import * as z from 'zod';

import config from '@/config';

export const TABLET_BREAKPOINT = 767;
export const DESKTOP_BREAKPOINT = 1024;
export const SERVICE_WORKER_CHANNEL_NAME = 'service-worker-channel';

export const ISSUE_LANG_KEY = 'issue-lang';
export const USER_ROLE = 'user-role';
export const FAVORITE_KEY = 'favorites';

export const ACCESS_TOKEN_KEY = 'access_token';
export const ACCESS_TOKEN_EXPIRE_AT_KEY = 'token-expired-at';
export const BUILD_NUMBER = 'build';

export const APP_USER_ID = 'app-user-id';

export const ALERT_TIMEOUT_KEYS = {
	ID: 'alert-timeout-id',
	DATETIME: 'alert-timeout-datetime',
};

export const NAVBAR_ICON_URL =
	config.build.assetDirectory && config.build.assetDirectory !== '/'
		? `${config.build.assetDirectory}/assets/navbar-icon.png`
		: `/assets/navbar-icon.png?build=${config.build.buildNumber}`;

export const APP_ICON_URL =
	config.build.assetDirectory && config.build.assetDirectory !== '/'
		? `${config.build.assetDirectory}/assets/favicon-196.png?build=${config.build.buildNumber}`
		: `/assets/favicon-196.png?build=${config.build.buildNumber}`;

export const NavbarIconLogo = () => {
	const { website } = config.build;
	if (website) {
		return (
			// eslint-disable-next-line jsx-a11y/anchor-is-valid
			<a rel="noreferrer" href={website} target="_blank">
				<img src={NAVBAR_ICON_URL} alt="Logo" />
			</a>
		);
	}
	return (
		<Link to={'/'}>
			<img src={NAVBAR_ICON_URL} alt="Logo" />
		</Link>
	);
};

export const LangEnum = z.enum([...config.build.languages]).optional();

export const basepath = config?.build?.subdirectory
	? config.build.subdirectory
	: undefined;

export const MANIFEST_ICON_URL = config.build.assetDirectory
	? `${
			config.build.assetDirectory === '/'
				? ''
				: config.build.assetDirectory
		}/assets/manifest-icon-512.png?build=${config.build.buildNumber}`
	: `/assets/manifest-icon-512.png?build=${config.build.buildNumber}`;

export function reduceExcludeComponents(
	acc: Array<any>,
	component: any,
): Array<any> {
	component = clone(component);
	const { excludedchannels } = component;
	if (component.columns) {
		component.columns = component.columns.map((column: Array<any>) => {
			return column ? column.reduce(reduceExcludeComponents, []) : column;
		});
	}
	if (!excludedchannels) {
		acc.push(component);
		return acc;
	}

	const excludedChannels = new Set([...excludedchannels]);
	if (excludedChannels.has('app')) {
		return acc;
	}
	acc.push(component);
	return acc;
}

export interface Issue {
	id: string;
	name: any;
	description: string;
	index: number;
	cover: string;
	created?: any;
	features: Array<string>;
	isFree: boolean;
	releaseDate?: any;
	updated?: any;
	articles?: Array<Article>;
	styles?: Array<Style>;
	featuredArticles?: Array<Article>;
	pages: any;
	slug: string;
	externalUrl: string;
}

export interface Section {
	id: string;
	name: string;
	position: number;
	issues?: Array<Issue>;
}

export interface Article {
	id: string;
	role: 'advert' | 'article';
	ArticleID?: string;
	name?: any;
	author?: null | string;
	description?: string;
	thumbnail?: string;
	featureImage?: string;
	features: Array<string>;
	index: number;
	style?: string;
	components?: Array<any>;
	created?: any;
	updated?: any;
	releaseDate?: any;
	IssueID?: string;
	issueSlug?: string;
	keywords?: Array<string>;
	styles?: Array<Style>;
	audio?: string;
	pages?: Array<string>;
	channel?: any;
	slug?: string;
	isFeatured: boolean;
}

export function fixInvalidInPages(page: string) {
	if (!page.replace) {
		return page;
	}
	return page
		.replace(
			/https:\/\/cdn\.pwa\.canvasflow\.io\/https:\/\/cdn\.pwa\.canvasflow\.io/,
			'https://cdn.pwa.canvasflow.io',
		)
		.replace(
			/https:\/\/cdn\.pwa\.cflowdev\.com\/https:\/\/cdn\.pwa\.cflowdev\.com/,
			'https://cdn.pwa.cflowdev.com',
		);
}

export function getImagesFromIssue(images: Set<string>, issues: Array<Issue>) {
	for (const issue of issues) {
		const { cover, featuredArticles } = issue;
		images.add(cover);
		if (!featuredArticles) {
			continue;
		}

		for (const article of featuredArticles) {
			const { thumbnail, featureImage } = article;
			if (thumbnail) {
				images.add(thumbnail);
			}

			if (featureImage) {
				images.add(featureImage);
			}
		}
	}
}

export interface Style {
	id: string;
	name: string;
	description: string;
	properties: any;
	supportedDevices: Set<DeviceType>;
	type: string;
	parent: string | null;
	tablet: string | null;
	desktop: string | null;
	created?: any;
	lastModified?: any;
}

export interface Font {
	name: string;
	urls: Array<string>;
}

export type DeviceType = 'mobile' | 'desktop' | 'tablet';

export function getAppToken(): undefined | string | null {
	if (!localStorage.getItem(ACCESS_TOKEN_KEY)) {
		return undefined;
	}

	return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export const GET_STYLES_QUERY = `
	{
		styles {
			id
			name
			description
			properties
			supportedDevices
			type
			parent
			tablet
			desktop
			created
			lastModified
		}
	}
`;

export function clone(obj: any): any {
	if (structuredClone) {
		return structuredClone(obj);
	}

	return obj ? JSON.parse(JSON.stringify(obj)) : obj;
}

export function getAppUser(): undefined | string | null {
	const userId = localStorage.getItem(APP_USER_ID);
	if (!userId) {
		return undefined;
	}

	if (
		config?.app?.auth?.enabled &&
		(config?.app?.auth?.type === 'jwt' ||
			config?.app?.auth?.type === 'cognito')
	) {
		return userId;
	}

	return undefined;
}

export function getAccessToken(): undefined | string | null {
	let accessToken = undefined;
	if (getUserRole() === 'admin') {
		return getAppToken();
	}

	accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
	if (!accessToken) {
		return undefined;
	}

	if (
		config?.app?.auth?.enabled &&
		(config?.app?.auth?.type === 'jwt' ||
			config?.app?.auth?.type === 'cognito')
	) {
		return accessToken;
	}

	return undefined;
}

export function getUserRole(): undefined | string {
	const role = localStorage.getItem(USER_ROLE);
	if (!role) {
		return undefined;
	}

	return role;
}

export function isNumeric(num: any) {
	return !isNaN(num);
}

export type NavbarVisibility = 'visible' | 'hidden';

export const GET_ISSUE_BY_ID_QUERY = `
	query Issue($id: ID!) {
		issue(id: $id) {
			id
			name
			description
			textDirection
			externalUrl
			pages
			cover
			price
			currency
			slug
			data
			releaseDate
			updated
			isPaid
			created
			isPurchased
			styles {
				id
				name
				description
				properties
				supportedDevices
				type
				parent
				tablet
				desktop
				created
				lastModified
			}
			articles {
				id
				ArticleID
				thumbnail
				author
				style
				name
				role
				slug
				keywords
				audios {
	  	    id
					url
				  lang
				}
				channel
				index
				description
				pages
				components
				styleProperties
				style
				updated
				isFeatured
				replica {
					name
					page
					image
					hotspots {
						url
						area {
							x
							y
							width
							height
						}
					}
				}
			}
		}
	}
`;

// TODO Revert back to get features
export const HACK_GET_ISSUE_BY_ID_QUERY = `
	query Issue($id: ID!) {
		issue(id: $id) {
			id
			name
			description
			textDirection
			externalUrl
			pages
			cover
			price
			currency
			slug
			data
			releaseDate
			updated
			isPaid
			created
			isPurchased
			articles {
				id
				ArticleID
				thumbnail
				style
				name
				role
				slug
				keywords
				audio
				channel
				index
				description
				pages
				components
				styleProperties
				style
				updated
				isFeatured
				replica {
					name
					page
					image
					hotspots {
						url
						area {
							x
							y
							width
							height
						}
					}
				}
			}
		}
	}
`;

export const LANGUAGES: any = {
	en: {
		ar: 'Arabic',
		bg: 'Bulgarian',
		cs: 'Czech',
		da: 'Danish',
		de: 'German',
		el: 'Greek',
		en: 'English',
		es: 'Spanish',
		et: 'Estonian',
		fi: 'Finnish',
		fr: 'French',
		he: 'Hebrew',
		hu: 'Hungarian',
		id: 'Indonesian',
		it: 'Italian',
		ja: 'Japanese',
		ko: 'Korean',
		lt: 'Lithuanian',
		lv: 'Latvian',
		nb: 'Norwegian',
		nl: 'Dutch',
		pl: 'Polish',
		pt: 'Portuguese',
		ro: 'Romanian',
		ru: 'Russian',
		sk: 'Slovak',
		sl: 'Slovenian',
		sv: 'Swedish',
		th: 'Thai',
		tr: 'Turkish',
		uk: 'Ukrainian',
		vi: 'Vietnamese',
		zh: 'Chinese',
	},
	ar: {
		ar: 'العربية',
		bg: 'البلغارية',
		cs: 'التشيكية',
		da: 'الدنماركية',
		de: 'الألمانية',
		el: 'اليونانية',
		en: 'الإنجليزية',
		es: 'الإسبانية',
		et: 'الإستونية',
		fi: 'الفنلندية',
		fr: 'الفرنسية',
		he: 'العبرية',
		hu: 'الهنغارية',
		id: 'الإندونيسية',
		it: 'الإيطالية',
		ja: 'اليابانية',
		ko: 'الكورية',
		lt: 'الليتوانية',
		lv: 'اللاتفية',
		nb: 'النرويجية',
		nl: 'الهولندية',
		pl: 'البولندية',
		pt: 'البرتغالية',
		ro: 'الرومانية',
		ru: 'الروسية',
		sk: 'السلوفاكية',
		sl: 'السلوفينية',
		sv: 'السويدية',
		th: 'التايلاندية',
		tr: 'التركية',
		uk: 'الأوكرانية',
		vi: 'الفيتنامية',
		zh: 'الصينية',
	},
	bg: {
		ar: 'Арабски',
		bg: 'Български',
		cs: 'Чешки',
		da: 'Датски',
		de: 'Немски',
		el: 'Гръцки',
		en: 'Английски',
		es: 'Испански',
		et: 'Естонски',
		fi: 'Фински',
		fr: 'Френски',
		he: 'Иврит',
		hu: 'Унгарски',
		id: 'Индонезийски',
		it: 'Италиански',
		ja: 'Японски',
		ko: 'Корейски',
		lt: 'Литовски',
		lv: 'Латвийски',
		nb: 'Норвежки',
		nl: 'Нидерландски',
		pl: 'Полски',
		pt: 'Португалски',
		ro: 'Румънски',
		ru: 'Руски',
		sk: 'Словашки',
		sl: 'Словенски',
		sv: 'Шведски',
		th: 'Тайски',
		tr: 'Турски',
		uk: 'Украински',
		vi: 'Виетнамски',
		zh: 'Китайски',
	},
	cs: {
		ar: 'Arabština',
		bg: 'Bulharština',
		cs: 'Čeština',
		da: 'Dánština',
		de: 'Němčina',
		el: 'Řečtina',
		en: 'Angličtina',
		es: 'Španělština',
		et: 'Estonština',
		fi: 'Finština',
		fr: 'Francouzština',
		he: 'Hebrejština',
		hu: 'Maďarština',
		id: 'Indonéština',
		it: 'Italština',
		ja: 'Japonština',
		ko: 'Korejština',
		lt: 'Litevština',
		lv: 'Lotyština',
		nb: 'Norština',
		nl: 'Nizozemština',
		pl: 'Polština',
		pt: 'Portugalština',
		ro: 'Rumunština',
		ru: 'Ruština',
		sk: 'Slovenština',
		sl: 'Slovinština',
		sv: 'Švédština',
		th: 'Thajština',
		tr: 'Turečtina',
		uk: 'Ukrajinština',
		vi: 'Vietnamština',
		zh: 'Čínština',
	},
	da: {
		ar: 'Arabisk',
		bg: 'Bulgarsk',
		cs: 'Tjekkisk',
		da: 'Dansk',
		de: 'Tysk',
		el: 'Græsk',
		en: 'Engelsk',
		es: 'Spansk',
		et: 'Estisk',
		fi: 'Finsk',
		fr: 'Fransk',
		he: 'Hebraisk',
		hu: 'Ungarsk',
		id: 'Indonesisk',
		it: 'Italiensk',
		ja: 'Japansk',
		ko: 'Koreansk',
		lt: 'Litauisk',
		lv: 'Lettisk',
		nb: 'Norsk',
		nl: 'Nederlandsk',
		pl: 'Polsk',
		pt: 'Portugisisk',
		ro: 'Rumænsk',
		ru: 'Russisk',
		sk: 'Slovakisk',
		sl: 'Slovensk',
		sv: 'Svensk',
		th: 'Thailandsk',
		tr: 'Tyrkisk',
		uk: 'Ukrainsk',
		vi: 'Vietnamesisk',
		zh: 'Kinesisk',
	},
	de: {
		ar: 'Arabisch',
		bg: 'Bulgarisch',
		cs: 'Tschechisch',
		da: 'Dänisch',
		de: 'Deutsch',
		el: 'Griechisch',
		en: 'Englisch',
		es: 'Spanisch',
		et: 'Estnisch',
		fi: 'Finnisch',
		fr: 'Französisch',
		he: 'Hebräisch',
		hu: 'Ungarisch',
		id: 'Indonesisch',
		it: 'Italienisch',
		ja: 'Japanisch',
		ko: 'Koreanisch',
		lt: 'Litauisch',
		lv: 'Lettisch',
		nb: 'Norwegisch',
		nl: 'Niederländisch',
		pl: 'Polnisch',
		pt: 'Portugiesisch',
		ro: 'Rumänisch',
		ru: 'Russisch',
		sk: 'Slowakisch',
		sl: 'Slowenisch',
		sv: 'Schwedisch',
		th: 'Thailändisch',
		tr: 'Türkisch',
		uk: 'Ukrainisch',
		vi: 'Vietnamesisch',
		zh: 'Chinesisch',
	},
	el: {
		ar: 'Αραβικά',
		bg: 'Βουλγαρικά',
		cs: 'Τσεχικά',
		da: 'Δανικά',
		de: 'Γερμανικά',
		el: 'Ελληνικά',
		en: 'Αγγλικά',
		es: 'Ισπανικά',
		et: 'Εσθονικά',
		fi: 'Φινλανδικά',
		fr: 'Γαλλικά',
		he: 'Εβραϊκά',
		hu: 'Ουγγρικά',
		id: 'Ινδονησιακά',
		it: 'Ιταλικά',
		ja: 'Ιαπωνικά',
		ko: 'Κορεατικά',
		lt: 'Λιθουανικά',
		lv: 'Λεττονικά',
		nb: 'Νορβηγικά',
		nl: 'Ολλανδικά',
		pl: 'Πολωνικά',
		pt: 'Πορτογαλικά',
		ro: 'Ρουμανικά',
		ru: 'Ρωσικά',
		sk: 'Σλοβακικά',
		sl: 'Σλοβενικά',
		sv: 'Σουηδικά',
		th: 'Ταϊλανδικά',
		tr: 'Τουρκικά',
		uk: 'Ουκρανικά',
		vi: 'Βιετναμικά',
		zh: 'Κινεζικά',
	},
	es: {
		ar: 'Árabe',
		bg: 'Búlgaro',
		cs: 'Checo',
		da: 'Danés',
		de: 'Alemán',
		el: 'Griego',
		en: 'Inglés',
		es: 'Español',
		et: 'Estonio',
		fi: 'Finlandés',
		fr: 'Francés',
		he: 'Hebreo',
		hu: 'Húngaro',
		id: 'Indonesio',
		it: 'Italiano',
		ja: 'Japonés',
		ko: 'Coreano',
		lt: 'Lituano',
		lv: 'Letón',
		nb: 'Noruego',
		nl: 'Neerlandés',
		pl: 'Polaco',
		pt: 'Portugués',
		ro: 'Rumano',
		ru: 'Ruso',
		sk: 'Eslovaco',
		sl: 'Esloveno',
		sv: 'Sueco',
		th: 'Tailandés',
		tr: 'Turco',
		uk: 'Ucraniano',
		vi: 'Vietnamita',
		zh: 'Chino',
	},
	et: {
		ar: 'Araabia',
		bg: 'Bulgaaria',
		cs: 'Tšehhi',
		da: 'Taani',
		de: 'Saksa',
		el: 'Kreeka',
		en: 'Inglise',
		es: 'Hispaania',
		et: 'Eesti',
		fi: 'Soome',
		fr: 'Prantsuse',
		he: 'Heebrea',
		hu: 'Ungari',
		id: 'Indoneesia',
		it: 'Itaalia',
		ja: 'Jaapani',
		ko: 'Korea',
		lt: 'Leedu',
		lv: 'Läti',
		nb: 'Norra',
		nl: 'Hollandi',
		pl: 'Poola',
		pt: 'Portugali',
		ro: 'Rumeenia',
		ru: 'Vene',
		sk: 'Slovaki',
		sl: 'Sloveeni',
		sv: 'Rootsi',
		th: 'Tai',
		tr: 'Türgi',
		uk: 'Ukraina',
		vi: 'Vietnami',
		zh: 'Hiina',
	},
	fi: {
		ar: 'Arabia',
		bg: 'Bulgaria',
		cs: 'Tšekki',
		da: 'Tanska',
		de: 'Saksa',
		el: 'Kreikka',
		en: 'Englanti',
		es: 'Espanja',
		et: 'Viro',
		fi: 'Suomi',
		fr: 'Ranska',
		he: 'Heprea',
		hu: 'Unkari',
		id: 'Indonesia',
		it: 'Italia',
		ja: 'Japani',
		ko: 'Korea',
		lt: 'Liettua',
		lv: 'Latvia',
		nb: 'Norja',
		nl: 'Hollanti',
		pl: 'Puola',
		pt: 'Portugali',
		ro: 'Romania',
		ru: 'Venäjä',
		sk: 'Slovakia',
		sl: 'Slovenia',
		sv: 'Ruotsi',
		th: 'Thai',
		tr: 'Turkki',
		uk: 'Ukraina',
		vi: 'Vietnam',
		zh: 'Kiina',
	},
	fr: {
		ar: 'Arabe',
		bg: 'Bulgare',
		cs: 'Tchèque',
		da: 'Danois',
		de: 'Allemand',
		el: 'Grec',
		en: 'Anglais',
		es: 'Espagnol',
		et: 'Estonien',
		fi: 'Finnois',
		fr: 'Français',
		he: 'Hébreu',
		hu: 'Hongrois',
		id: 'Indonésien',
		it: 'Italien',
		ja: 'Japonais',
		ko: 'Coréen',
		lt: 'Lituanien',
		lv: 'Letton',
		nb: 'Norvégien',
		nl: 'Néerlandais',
		pl: 'Polonais',
		pt: 'Portugais',
		ro: 'Roumain',
		ru: 'Russe',
		sk: 'Slovaque',
		sl: 'Slovène',
		sv: 'Suédois',
		th: 'Thaï',
		tr: 'Turc',
		uk: 'Ukrainien',
		vi: 'Vietnamien',
		zh: 'Chinois',
	},
	he: {
		ar: 'ערבית',
		bg: 'בולגרית',
		cs: "צ'כית",
		da: 'דנית',
		de: 'גרמנית',
		el: 'יוונית',
		en: 'אנגלית',
		es: 'ספרדית',
		et: 'אסטונית',
		fi: 'פינית',
		fr: 'צרפתית',
		he: 'עברית',
		hu: 'הונגרית',
		id: 'אינדונזית',
		it: 'איטלקית',
		ja: 'יפנית',
		ko: 'קוריאנית',
		lt: 'ליטאית',
		lv: 'לטבית',
		nb: 'נורווגית',
		nl: 'הולנדית',
		pl: 'פולנית',
		pt: 'פורטוגזית',
		ro: 'רומנית',
		ru: 'רוסית',
		sk: 'סלובקית',
		sl: 'סלובנית',
		sv: 'שוודית',
		th: 'תאית',
		tr: 'טורקית',
		uk: 'אוקראינית',
		vi: 'וייטנאמית',
		zh: 'סינית',
	},
	hu: {
		ar: 'Arab',
		bg: 'Bolgár',
		cs: 'Cseh',
		da: 'Dán',
		de: 'Német',
		el: 'Görög',
		en: 'Angol',
		es: 'Spanyol',
		et: 'Észt',
		fi: 'Finn',
		fr: 'Francia',
		he: 'Héber',
		hu: 'Magyar',
		id: 'Indonéz',
		it: 'Olasz',
		ja: 'Japán',
		ko: 'Koreai',
		lt: 'Litván',
		lv: 'Lett',
		nb: 'Norvég',
		nl: 'Holland',
		pl: 'Lengyel',
		pt: 'Portugál',
		ro: 'Román',
		ru: 'Orosz',
		sk: 'Szlovák',
		sl: 'Szlovén',
		sv: 'Svéd',
		th: 'Thai',
		tr: 'Török',
		uk: 'Ukrán',
		vi: 'Vietnami',
		zh: 'Kínai',
	},
	id: {
		ar: 'Arab',
		bg: 'Bulgaria',
		cs: 'Ceko',
		da: 'Denmark',
		de: 'Jerman',
		el: 'Yunani',
		en: 'Inggris',
		es: 'Spanyol',
		et: 'Estonia',
		fi: 'Finlandia',
		fr: 'Prancis',
		he: 'Ibrani',
		hu: 'Hungaria',
		id: 'Indonesia',
		it: 'Italia',
		ja: 'Jepang',
		ko: 'Korea',
		lt: 'Lituania',
		lv: 'Latvia',
		nb: 'Norwegia',
		nl: 'Belanda',
		pl: 'Polandia',
		pt: 'Portugis',
		ro: 'Rumania',
		ru: 'Rusia',
		sk: 'Slowakia',
		sl: 'Slovenia',
		sv: 'Swedia',
		th: 'Thailand',
		tr: 'Turki',
		uk: 'Ukraina',
		vi: 'Vietnam',
		zh: 'Tionghoa',
	},
	it: {
		ar: 'Arabo',
		bg: 'Bulgaro',
		cs: 'Ceco',
		da: 'Danese',
		de: 'Tedesco',
		el: 'Greco',
		en: 'Inglese',
		es: 'Spagnolo',
		et: 'Estone',
		fi: 'Finlandese',
		fr: 'Francese',
		he: 'Ebraico',
		hu: 'Ungherese',
		id: 'Indonesiano',
		it: 'Italiano',
		ja: 'Giapponese',
		ko: 'Coreano',
		lt: 'Lituano',
		lv: 'Lettone',
		nb: 'Norvegese',
		nl: 'Olandese',
		pl: 'Polacco',
		pt: 'Portoghese',
		ro: 'Rumeno',
		ru: 'Russo',
		sk: 'Slovacco',
		sl: 'Sloveno',
		sv: 'Svedese',
		th: 'Tailandese',
		tr: 'Turco',
		uk: 'Ucraino',
		vi: 'Vietnamita',
		zh: 'Cinese',
	},
	ja: {
		ar: 'アラビア語',
		bg: 'ブルガリア語',
		cs: 'チェコ語',
		da: 'デンマーク語',
		de: 'ドイツ語',
		el: 'ギリシャ語',
		en: '英語',
		es: 'スペイン語',
		et: 'エストニア語',
		fi: 'フィンランド語',
		fr: 'フランス語',
		he: 'ヘブライ語',
		hu: 'ハンガリー語',
		id: 'インドネシア語',
		it: 'イタリア語',
		ja: '日本語',
		ko: '韓国語',
		lt: 'リトアニア語',
		lv: 'ラトビア語',
		nb: 'ノルウェー語',
		nl: 'オランダ語',
		pl: 'ポーランド語',
		pt: 'ポルトガル語',
		ro: 'ルーマニア語',
		ru: 'ロシア語',
		sk: 'スロバキア語',
		sl: 'スロベニア語',
		sv: 'スウェーデン語',
		th: 'タイ語',
		tr: 'トルコ語',
		uk: 'ウクライナ語',
		vi: 'ベトナム語',
		zh: '中国語',
	},
	ko: {
		ar: '아랍어',
		bg: '불가리아어',
		cs: '체코어',
		da: '덴마크어',
		de: '독일어',
		el: '그리스어',
		en: '영어',
		es: '스페인어',
		et: '에스토니아어',
		fi: '핀란드어',
		fr: '프랑스어',
		he: '히브리어',
		hu: '헝가리어',
		id: '인도네시아어',
		it: '이탈리아어',
		ja: '일본어',
		ko: '한국어',
		lt: '리투아니아어',
		lv: '라트비아어',
		nb: '노르웨이어',
		nl: '네덜란드어',
		pl: '폴란드어',
		pt: '포르투갈어',
		ro: '루마니아어',
		ru: '러시아어',
		sk: '슬로바키아어',
		sl: '슬로베니아어',
		sv: '스웨덴어',
		th: '태국어',
		tr: '터키어',
		uk: '우크라이나어',
		vi: '베트남어',
		zh: '중국어',
	},
	lt: {
		ar: 'Arabų',
		bg: 'Bulgarų',
		cs: 'Čekų',
		da: 'Danų',
		de: 'Vokiečių',
		el: 'Graikų',
		en: 'Anglų',
		es: 'Ispanų',
		et: 'Estų',
		fi: 'Suomių',
		fr: 'Prancūzų',
		he: 'Hebrajų',
		hu: 'Vengrų',
		id: 'Indonezų',
		it: 'Italų',
		ja: 'Japonų',
		ko: 'Korėjiečių',
		lt: 'Lietuvių',
		lv: 'Latvių',
		nb: 'Norvegų',
		nl: 'Olandų',
		pl: 'Lenkų',
		pt: 'Portugalų',
		ro: 'Rumunų',
		ru: 'Rusų',
		sk: 'Slovakų',
		sl: 'Slovėnų',
		sv: 'Švedų',
		th: 'Tajų',
		tr: 'Turkų',
		uk: 'Ukrainiečių',
		vi: 'Vietnamiečių',
		zh: 'Kinų',
	},
	lv: {
		ar: 'Arābu',
		bg: 'Bulgāru',
		cs: 'Čehu',
		da: 'Dāņu',
		de: 'Vācu',
		el: 'Grieķu',
		en: 'Angļu',
		es: 'Spāņu',
		et: 'Igauņu',
		fi: 'Somu',
		fr: 'Franču',
		he: 'Ivrits',
		hu: 'Ungāru',
		id: 'Indonēziešu',
		it: 'Itāļu',
		ja: 'Japāņu',
		ko: 'Korejiešu',
		lt: 'Lietuviešu',
		lv: 'Latviešu',
		nb: 'Norvēģu',
		nl: 'Holandiešu',
		pl: 'Poļu',
		pt: 'Portugāļu',
		ro: 'Rumāņu',
		ru: 'Krievu',
		sk: 'Slovāku',
		sl: 'Slovēņu',
		sv: 'Zviedru',
		th: 'Taju',
		tr: 'Turku',
		uk: 'Ukraiņu',
		vi: 'Vjetnamiešu',
		zh: 'Ķīniešu',
	},
	nb: {
		ar: 'Arabisk',
		bg: 'Bulgarsk',
		cs: 'Tsjekkisk',
		da: 'Dansk',
		de: 'Tysk',
		el: 'Gresk',
		en: 'Engelsk',
		es: 'Spansk',
		et: 'Estisk',
		fi: 'Finsk',
		fr: 'Fransk',
		he: 'Hebraisk',
		hu: 'Ungarsk',
		id: 'Indonesisk',
		it: 'Italiensk',
		ja: 'Japansk',
		ko: 'Koreansk',
		lt: 'Litauisk',
		lv: 'Latvisk',
		nb: 'Norsk',
		nl: 'Nederlandsk',
		pl: 'Polsk',
		pt: 'Portugisisk',
		ro: 'Rumensk',
		ru: 'Russisk',
		sk: 'Slovakisk',
		sl: 'Slovensk',
		sv: 'Svensk',
		th: 'Thai',
		tr: 'Tyrkisk',
		uk: 'Ukrainsk',
		vi: 'Vietnamesisk',
		zh: 'Kinesisk',
	},
	nl: {
		ar: 'Arabisch',
		bg: 'Bulgaars',
		cs: 'Tsjechisch',
		da: 'Deens',
		de: 'Duits',
		el: 'Grieks',
		en: 'Engels',
		es: 'Spaans',
		et: 'Ests',
		fi: 'Fins',
		fr: 'Frans',
		he: 'Hebreeuws',
		hu: 'Hongaars',
		id: 'Indonesisch',
		it: 'Italiaans',
		ja: 'Japans',
		ko: 'Koreaans',
		lt: 'Litouws',
		lv: 'Lets',
		nb: 'Noors',
		nl: 'Nederlands',
		pl: 'Pools',
		pt: 'Portugees',
		ro: 'Roemeens',
		ru: 'Russisch',
		sk: 'Slowaaks',
		sl: 'Sloveens',
		sv: 'Zweeds',
		th: 'Thais',
		tr: 'Turks',
		uk: 'Oekraïens',
		vi: 'Vietnamees',
		zh: 'Chinees',
	},
	pl: {
		ar: 'Arabski',
		bg: 'Bułgarski',
		cs: 'Czeski',
		da: 'Duński',
		de: 'Niemiecki',
		el: 'Grecki',
		en: 'Angielski',
		es: 'Hiszpański',
		et: 'Estoński',
		fi: 'Fiński',
		fr: 'Francuski',
		he: 'Hebrajski',
		hu: 'Węgierski',
		id: 'Indonezyjski',
		it: 'Włoski',
		ja: 'Japoński',
		ko: 'Koreański',
		lt: 'Litewski',
		lv: 'Łotewski',
		nb: 'Norweski',
		nl: 'Niderlandzki',
		pl: 'Polski',
		pt: 'Portugalski',
		ro: 'Rumuński',
		ru: 'Rosyjski',
		sk: 'Słowacki',
		sl: 'Słoweński',
		sv: 'Szwedzki',
		th: 'Tajski',
		tr: 'Turecki',
		uk: 'Ukraiński',
		vi: 'Wietnamski',
		zh: 'Chiński',
	},
	pt: {
		ar: 'Árabe',
		bg: 'Búlgaro',
		cs: 'Checo',
		da: 'Dinamarquês',
		de: 'Alemão',
		el: 'Grego',
		en: 'Inglês',
		es: 'Espanhol',
		et: 'Estoniano',
		fi: 'Finlandês',
		fr: 'Francês',
		he: 'Hebraico',
		hu: 'Húngaro',
		id: 'Indonésio',
		it: 'Italiano',
		ja: 'Japonês',
		ko: 'Coreano',
		lt: 'Lituano',
		lv: 'Letão',
		nb: 'Norueguês',
		nl: 'Neerlandês',
		pl: 'Polaco',
		pt: 'Português',
		ro: 'Romeno',
		ru: 'Russo',
		sk: 'Eslovaco',
		sl: 'Esloveno',
		sv: 'Sueco',
		th: 'Tailandês',
		tr: 'Turco',
		uk: 'Ucraniano',
		vi: 'Vietnamita',
		zh: 'Chinês',
	},
	ro: {
		ar: 'Arabă',
		bg: 'Bulgară',
		cs: 'Cehă',
		da: 'Daneză',
		de: 'Germană',
		el: 'Greacă',
		en: 'Engleză',
		es: 'Spaniolă',
		et: 'Estonă',
		fi: 'Finlandeză',
		fr: 'Franceză',
		he: 'Ebraică',
		hu: 'Maghiară',
		id: 'Indoneziană',
		it: 'Italiană',
		ja: 'Japoneză',
		ko: 'Coreeană',
		lt: 'Lituaniană',
		lv: 'Letonă',
		nb: 'Norvegiană',
		nl: 'Olandeză',
		pl: 'Poloneză',
		pt: 'Portugheză',
		ro: 'Română',
		ru: 'Rusă',
		sk: 'Slovacă',
		sl: 'Slovenă',
		sv: 'Suedeză',
		th: 'Thailandeză',
		tr: 'Turcă',
		uk: 'Ucraineană',
		vi: 'Vietnameză',
		zh: 'Chineză',
	},
	ru: {
		ar: 'Арабский',
		bg: 'Болгарский',
		cs: 'Чешский',
		da: 'Датский',
		de: 'Немецкий',
		el: 'Греческий',
		en: 'Английский',
		es: 'Испанский',
		et: 'Эстонский',
		fi: 'Финский',
		fr: 'Французский',
		he: 'Иврит',
		hu: 'Венгерский',
		id: 'Индонезийский',
		it: 'Итальянский',
		ja: 'Японский',
		ko: 'Корейский',
		lt: 'Литовский',
		lv: 'Латышский',
		nb: 'Норвежский',
		nl: 'Нидерландский',
		pl: 'Польский',
		pt: 'Португальский',
		ro: 'Румынский',
		ru: 'Русский',
		sk: 'Словацкий',
		sl: 'Словенский',
		sv: 'Шведский',
		th: 'Тайский',
		tr: 'Турецкий',
		uk: 'Украинский',
		vi: 'Вьетнамский',
		zh: 'Китайский',
	},
	sk: {
		ar: 'Arabčina',
		bg: 'Bulharčina',
		cs: 'Čeština',
		da: 'Dánčina',
		de: 'Nemčina',
		el: 'Gréčtina',
		en: 'Angličtina',
		es: 'Španielčina',
		et: 'Estónčina',
		fi: 'Fínčina',
		fr: 'Francúzština',
		he: 'Hebrejčina',
		hu: 'Maďarčina',
		id: 'Indonézština',
		it: 'Taliančina',
		ja: 'Japončina',
		ko: 'Kórejčina',
		lt: 'Litovčina',
		lv: 'Lotyština',
		nb: 'Nórčina',
		nl: 'Holandčina',
		pl: 'Poľština',
		pt: 'Portugalčina',
		ro: 'Rumunčina',
		ru: 'Ruština',
		sk: 'Slovenčina',
		sl: 'Slovinčina',
		sv: 'Švédčina',
		th: 'Thajčina',
		tr: 'Turečtina',
		uk: 'Ukrajinčina',
		vi: 'Vietnamčina',
		zh: 'Čínština',
	},
	sl: {
		ar: 'Arabščina',
		bg: 'Bolgarščina',
		cs: 'Češčina',
		da: 'Danščina',
		de: 'Nemščina',
		el: 'Grščina',
		en: 'Angleščina',
		es: 'Španščina',
		et: 'Estonščina',
		fi: 'Finščina',
		fr: 'Francoščina',
		he: 'Hebrejščina',
		hu: 'Madžarščina',
		id: 'Indonezijščina',
		it: 'Italijanščina',
		ja: 'Japonščina',
		ko: 'Korejščina',
		lt: 'Litvanščina',
		lv: 'Latvijščina',
		nb: 'Norveščina',
		nl: 'Nizozemščina',
		pl: 'Poljščina',
		pt: 'Portugalščina',
		ro: 'Romunščina',
		ru: 'Ruščina',
		sk: 'Slovaščina',
		sl: 'Slovenščina',
		sv: 'Švedščina',
		th: 'Tajščina',
		tr: 'Turščina',
		uk: 'Ukrajinščina',
		vi: 'Vietnamščina',
		zh: 'Kitajščina',
	},
	sv: {
		ar: 'Arabiska',
		bg: 'Bulgariska',
		cs: 'Tjeckiska',
		da: 'Danska',
		de: 'Tyska',
		el: 'Grekiska',
		en: 'Engelska',
		es: 'Spanska',
		et: 'Estniska',
		fi: 'Finska',
		fr: 'Franska',
		he: 'Hebreiska',
		hu: 'Ungerska',
		id: 'Indonesiska',
		it: 'Italienska',
		ja: 'Japanska',
		ko: 'Koreanska',
		lt: 'Litauiska',
		lv: 'Lettiska',
		nb: 'Norska',
		nl: 'Nederländska',
		pl: 'Polska',
		pt: 'Portugisiska',
		ro: 'Rumänska',
		ru: 'Ryska',
		sk: 'Slovakiska',
		sl: 'Slovenska',
		sv: 'Svenska',
		th: 'Thailändska',
		tr: 'Turkiska',
		uk: 'Ukrainska',
		vi: 'Vietnamesiska',
		zh: 'Kinesiska',
	},
	th: {
		ar: 'ภาษาอาหรับ',
		bg: 'ภาษาบัลแกเรีย',
		cs: 'ภาษาเช็ก',
		da: 'ภาษาเดนมาร์ก',
		de: 'ภาษาเยอรมัน',
		el: 'ภาษากรีก',
		en: 'ภาษาอังกฤษ',
		es: 'ภาษาสเปน',
		et: 'ภาษาเอสโตเนีย',
		fi: 'ภาษาฟินแลนด์',
		fr: 'ภาษาฝรั่งเศส',
		he: 'ภาษาฮีบรู',
		hu: 'ภาษาฮังการี',
		id: 'ภาษาอินโดนีเซีย',
		it: 'ภาษาอิตาลี',
		ja: 'ภาษาญี่ปุ่น',
		ko: 'ภาษาเกาหลี',
		lt: 'ภาษาลิทัวเนีย',
		lv: 'ภาษาลัตเวีย',
		nb: 'ภาษานอร์เวย์',
		nl: 'ภาษาดัตช์',
		pl: 'ภาษาโปแลนด์',
		pt: 'ภาษาโปรตุเกส',
		ro: 'ภาษาโรมาเนีย',
		ru: 'ภาษารัสเซีย',
		sk: 'ภาษาสโลวัก',
		sl: 'ภาษาสโลวีเนีย',
		sv: 'ภาษาสวีเดน',
		th: 'ภาษาไทย',
		tr: 'ภาษาตุรกี',
		uk: 'ภาษายูเครน',
		vi: 'ภาษาเวียดนาม',
		zh: 'ภาษาจีน',
	},
	tr: {
		ar: 'Arapça',
		bg: 'Bulgarca',
		cs: 'Çekçe',
		da: 'Danca',
		de: 'Almanca',
		el: 'Yunanca',
		en: 'İngilizce',
		es: 'İspanyolca',
		et: 'Estonca',
		fi: 'Fince',
		fr: 'Fransızca',
		he: 'İbranice',
		hu: 'Macarca',
		id: 'Endonezce',
		it: 'İtalyanca',
		ja: 'Japonca',
		ko: 'Korece',
		lt: 'Litvanca',
		lv: 'Letonca',
		nb: 'Norveçce',
		nl: 'Felemenkçe',
		pl: 'Lehçe',
		pt: 'Portekizce',
		ro: 'Rumence',
		ru: 'Rusça',
		sk: 'Slovakça',
		sl: 'Slovence',
		sv: 'İsveççe',
		th: 'Tayca',
		tr: 'Türkçe',
		uk: 'Ukraynaca',
		vi: 'Vietnamca',
		zh: 'Çince',
	},
	uk: {
		ar: 'Арабська',
		bg: 'Болгарська',
		cs: 'Чеська',
		da: 'Данська',
		de: 'Німецька',
		el: 'Грецька',
		en: 'Англійська',
		es: 'Іспанська',
		et: 'Естонська',
		fi: 'Фінська',
		fr: 'Французька',
		he: 'Іврит',
		hu: 'Угорська',
		id: 'Індонезійська',
		it: 'Італійська',
		ja: 'Японська',
		ko: 'Корейська',
		lt: 'Литовська',
		lv: 'Латвійська',
		nb: 'Норвезька',
		nl: 'Нідерландська',
		pl: 'Польська',
		pt: 'Португальська',
		ro: 'Румунська',
		ru: 'Російська',
		sk: 'Словацька',
		sl: 'Словенська',
		sv: 'Шведська',
		th: 'Тайська',
		tr: 'Турецька',
		uk: 'Українська',
		vi: "В'єтнамська",
		zh: 'Китайська',
	},
	vi: {
		ar: 'Tiếng Ả Rập',
		bg: 'Tiếng Bulgaria',
		cs: 'Tiếng Séc',
		da: 'Tiếng Đan Mạch',
		de: 'Tiếng Đức',
		el: 'Tiếng Hy Lạp',
		en: 'Tiếng Anh',
		es: 'Tiếng Tây Ban Nha',
		et: 'Tiếng Estonia',
		fi: 'Tiếng Phần Lan',
		fr: 'Tiếng Pháp',
		he: 'Tiếng Do Thái',
		hu: 'Tiếng Hungary',
		id: 'Tiếng Indonesia',
		it: 'Tiếng Ý',
		ja: 'Tiếng Nhật',
		ko: 'Tiếng Hàn',
		lt: 'Tiếng Litva',
		lv: 'Tiếng Latvia',
		nb: 'Tiếng Na Uy',
		nl: 'Tiếng Hà Lan',
		pl: 'Tiếng Ba Lan',
		pt: 'Tiếng Bồ Đào Nha',
		ro: 'Tiếng Romania',
		ru: 'Tiếng Nga',
		sk: 'Tiếng Slovak',
		sl: 'Tiếng Slovenia',
		sv: 'Tiếng Thụy Điển',
		th: 'Tiếng Thái',
		tr: 'Tiếng Thổ Nhĩ Kỳ',
		uk: 'Tiếng Ukraina',
		vi: 'Tiếng Việt',
		zh: 'Tiếng Trung',
	},
	zh: {
		ar: '阿拉伯语',
		bg: '保加利亚语',
		cs: '捷克语',
		da: '丹麦语',
		de: '德语',
		el: '希腊语',
		en: '英语',
		es: '西班牙语',
		et: '爱沙尼亚语',
		fi: '芬兰语',
		fr: '法语',
		he: '希伯来语',
		hu: '匈牙利语',
		id: '印度尼西亚语',
		it: '意大利语',
		ja: '日语',
		ko: '韩语',
		lt: '立陶宛语',
		lv: '拉脱维亚语',
		nb: '挪威语',
		nl: '荷兰语',
		pl: '波兰语',
		pt: '葡萄牙语',
		ro: '罗马尼亚语',
		ru: '俄语',
		sk: '斯洛伐克语',
		sl: '斯洛文尼亚语',
		sv: '瑞典语',
		th: '泰语',
		tr: '土耳其语',
		uk: '乌克兰语',
		vi: '越南语',
		zh: '中文',
	},
};

const RTL_LANGUAGES = new Set(['ar', 'he']);

export function isRtlLanguage(language?: string): boolean {
	if(language === undefined) return false
	return RTL_LANGUAGES.has(language);
}

// TOOD This functions is only for hacking features
export function mapIssuesNonImplementedFeatures(issue: Issue): Issue {
	issue = clone(issue);
	if (!issue.features) {
		issue.features = [];
	}

	if (issue.articles) {
		issue.articles = issue.articles.map(mapArticlesNonImplementedFeatures);
	}

	if (issue.featuredArticles) {
		issue.featuredArticles = issue.featuredArticles.map(
			mapArticlesNonImplementedFeatures,
		);
	}

	return issue;
}

// TOOD This functions is only for hacking features
export function mapArticlesNonImplementedFeatures(article: Article): Article {
	article = clone(article);
	if (!article.features) {
		article.features = [];
	}

	return article;
}
